import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePlayer } from "../context/PlayerContext";
import { dvContent } from "../lib/api";
import { Reveal } from "../components/Motion";
import { setMeta } from "../lib/seo";

function LockedView({ user }) {
  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#030303]" data-testid="dv-locked">
      <div className="signal-static" />
      <div className="mm-vignette" />
      <div className="relative text-center px-6 py-40">
        <span className="font-serif text-8xl md:text-9xl text-white/10">05</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4">THE FIFTH DIMENSION</h1>
        <p className="font-mono text-xs tracking-[0.45em] text-red-500/80 mt-6">
          {user && user.dimensionV === "review" ? "ACCESS UNDER REVIEW" : "ACCESS RESTRICTED — VERIFIED ACCESS REQUIRED"}
        </p>
        {user && user.dimensionV === "review" ? (
          <p className="text-white/45 max-w-md mx-auto mt-8 leading-relaxed" data-testid="dv-review-note">
            Your Dimension V access is temporarily under review. If you believe this is an error,
            contact us from the email associated with your order.
          </p>
        ) : (
          <p className="text-white/45 max-w-md mx-auto mt-8 leading-relaxed">
            Beyond the four known Dimensions. Access is reserved for verified owners of all four
            physical Dimensions — collected together through the Complete Set or separately.
          </p>
        )}
        <div className="flex flex-wrap justify-center gap-5 mt-12">
          <Link to="/complete-set" data-testid="dv-locked-learn"
            className="border border-white/25 text-white/80 font-mono text-[11px] tracking-[0.25em] px-8 py-4 hover:border-gold hover:text-gold transition-colors">
            LEARN HOW TO UNLOCK
          </Link>
          <Link to="/dimension-v/access" data-testid="dv-locked-redeem"
            className="bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-8 py-4 hover:bg-white transition-colors">
            REDEEM ACCESS KEY
          </Link>
        </div>
        {!user && (
          <p className="mt-10">
            <Link to="/login?next=/dimension-v" data-testid="dv-locked-signin"
              className="font-mono text-[10px] tracking-[0.3em] text-white/40 hover:text-white transition-colors">
              ALREADY VERIFIED? SIGN IN →
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}

function OpenView() {
  const [content, setContent] = useState(null);
  const { playDirect, current, playing, toggle } = usePlayer();

  useEffect(() => {
    dvContent().then(setContent).catch(() => setContent(false));
  }, []);

  if (content === null) return <div className="min-h-screen bg-[#030303]" />;
  if (content === false) return <LockedView user={{ dimensionV: "review" }} />;

  return (
    <div className="relative min-h-screen bg-[#020203] overflow-hidden" data-testid="dv-open">
      {content.artwork && (
        <>
          <img src={content.artwork} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#020203]/70 via-[#020203]/85 to-[#020203]" />
        </>
      )}
      <div className="mm-vignette" />
      <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 pt-40 pb-32">
        <Reveal>
          <p className="font-mono text-[10px] tracking-[0.55em] text-gold">ACCESS VERIFIED</p>
          <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl font-light text-white mt-6">{content.title || "DIMENSION V"}</h1>
          {content.statement && (
            <p className="font-serif text-2xl md:text-3xl font-light leading-snug text-white/75 mt-10 max-w-2xl">{content.statement}</p>
          )}
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-20">
            <p className="font-mono text-[10px] tracking-[0.35em] text-white/40 mb-6">THE FIFTH TRANSMISSION — STREAMING ONLY</p>
            {content.tracks?.length ? (
              <ul data-testid="dv-tracks">
                {content.tracks.map((t, i) => {
                  const cur = current?.title === t.title && current?.releaseTitle === "DIMENSION V";
                  return (
                    <li key={i} className="flex items-center gap-5 py-5 border-t border-white/10" data-testid={`dv-track-${i}`}>
                      <button
                        onClick={() => (cur ? toggle() : t.stream_path && playDirect({
                          title: t.title, src: t.stream_path, artwork: content.artwork,
                          releaseTitle: "DIMENSION V" }))}
                        disabled={!t.stream_path}
                        aria-label={`Play ${t.title}`}
                        data-testid={`dv-track-${i}-play`}
                        className={`w-10 h-10 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                          cur ? "border-gold bg-gold text-black"
                            : t.stream_path ? "border-gold/50 text-gold hover:bg-gold hover:text-black"
                            : "border-white/10 text-white/20"}`}
                      >
                        {cur && playing ? <Pause size={13} /> : <Play size={13} className="ml-0.5" />}
                      </button>
                      <span className="font-serif text-2xl font-light text-white flex-1">{t.title}</span>
                      <span className="font-mono text-[10px] text-white/40">{t.duration}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="font-mono text-[10px] tracking-[0.3em] text-white/30 border-t border-white/10 pt-8" data-testid="dv-empty">
                THE FIFTH TRANSMISSION IS BEING PREPARED. YOUR ACCESS IS PERMANENT — RETURN SOON.
              </p>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}

export default function DimensionV() {
  const { user } = useAuth();

  useEffect(() => {
    setMeta({
      title: "Dimension V — Motion & Meaning",
      description: "The Fifth Dimension. Verified access required.",
      path: "/dimension-v",
      noindex: true,
    });
  }, []);

  if (user === null) return <div className="min-h-screen bg-[#030303]" />;
  const entitled = user && user.dimensionV === "active";
  return entitled ? <OpenView /> : <LockedView user={user || null} />;
}
